import clsx from 'clsx/lite'
import React, { type ReactNode } from 'react'

export const InputSizesValues = ['normal', 'small']
export type InputSizes = (typeof InputSizesValues)[number]

export const InputTypesValues = [
  'date',
  'datetime-local',
  'email',
  'hidden',
  'month',
  'number',
  'password',
  'search',
  'tel',
  'text',
  'time',
  'url',
  'week',
]
type InputTypes = (typeof InputTypesValues)[number]

export const InputValidationsValues = ['error', 'success', 'warning']
type InputValidations = (typeof InputValidationsValues)[number]

export type BaseTextFieldProps = {
  disabled?: boolean
  endAdornments?: Array<{
    element: React.ReactNode
  }>
  fullWidth?: boolean
  hasFocus: boolean
  helper?: string
  input: React.ReactNode
  inputProps?: Record<string, unknown>
  label?: string
  name?: string
  placeholder?: string
  required?: boolean
  shrink?: boolean
  size?: InputSizes
  type?: InputTypes
  validation?: InputValidations
  value?: any
} & React.HTMLAttributes<HTMLElement>

export const BaseTextField: React.FC<BaseTextFieldProps> = ({
  disabled = false,
  endAdornments = null,
  fullWidth = false,
  hasFocus,
  helper,
  input,
  label,
  name,
  placeholder,
  size = 'normal',
  type = 'text',
  validation,
  value,
  className,
  ...props
}) => {
  const notched =
    hasFocus ||
    ['date', 'datetime-local', 'month', 'time', 'week'].includes(type) ||
    placeholder ||
    value

  return (
    <div
      className={clsx(
        'relative min-w-[280px] flex-col',
        fullWidth ? 'flex' : 'inline-block',
        disabled && 'opacity-50',
        className
      )}
      {...props}
    >
      <div
        className={clsx(
          'relative w-full overflow-hidden rounded-[5px] border border-solid bg-white-50 text-085',
          'focus-within:outline focus-within:outline-3 focus-within:outline-offset-4 focus-within:outline-border-focus',
          validation ? 'border-theme-error-main' : 'border-grey-200',
          size === 'small' ? 'h-10' : 'h-16',
          disabled
            ? 'border-grey-200'
            : 'hover:ring-2 hover:ring-grey-600 hover:ring-offset-0'
        )}
      >
        {label && size === 'normal' && (
          <label
            htmlFor={name}
            className={clsx(
              'pointer-events-none absolute left-[11px] right-[11px] z-[2] overflow-hidden text-ellipsis whitespace-nowrap rounded-s',
              value || notched ? 'top-3' : 'top-[23px]'
            )}
          >
            {label}
          </label>
        )}
        {input}
        {endAdornments && (
          <div
            className={clsx(
              'absolute right-3 top-1/2 z-10 flex -translate-y-1/2 items-center',
              notched && validation && 'right-3'
            )}
          >
            {endAdornments.map((endAdornment, id) => {
              let EndIconWithProps
              const EndIcon = endAdornment.element
              if (EndIcon) {
                const {
                  size = 24,
                  ...rest
                  // @ts-expect-error
                } = EndIcon.props

                // @ts-expect-error
                EndIconWithProps = React.cloneElement(EndIcon, {
                  size,
                  ...rest,
                }) as ReactNode
              }

              return (
                <div key={id} className="relative p-1.5">
                  {EndIconWithProps}
                </div>
              )
            })}
          </div>
        )}
      </div>
      {helper && (
        <div
          className={clsx(
            'my-2 text-075',
            validation && 'text-theme-error-main'
          )}
        >
          {helper}
        </div>
      )}
    </div>
  )
}
